import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Radio,
  RadioGroup,
  Select,
  Text,
} from '@chakra-ui/react'
import type { WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect, useState } from 'react'

import { SelectFormControl } from 'BrokerTsLib/src/components'
import { Datepicker } from '../components/Datepicker'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { InputGroup } from '../components/InputGroup'
import { RadioStack } from '../components/RadioStack'
import { TextareaGroup } from '../components/TextareaGroup'
import { useSelections } from '../hooks'
import { useGetVehicleInspectionDistricts, useGetVehiclePhotoDocumentationCodes } from '../hooks/useWizard'
import { insuranceParamsContext } from '../layouts/Wizard'
import { Step4Context } from '../pages/carkulka/wizard/4'

export const AdditionalVehicleInfo = () => {
  const {
    register,
    formState: { errors },
    getValues,
    watch,
    control,
    setValue,
    trigger
  } = useFormContext<WizardVehicleAdditionalData>()
  const { insuranceParams } = useContext(insuranceParamsContext)
  const { offer } = useContext(Step4Context)
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const licensePlateTypes = getValues('vehicle.licensePlateTypes')
  const {
    data: dataPurchaseMethod,
    isLoading: isLoadingPurchaseMethod,
    isFetching: isFetchingPurchaseMethod,
  } = useSelections(SelectionType.PurchaseMethod)
  const { data: inspectionDistrictsData } = useGetVehicleInspectionDistricts(offer?.offerId, { enabled: !!offer?.offerId })
  const { data: photoDocumentationCodesData } = useGetVehiclePhotoDocumentationCodes(offer?.offerId, { enabled: !!offer?.offerId })

  const invoiceDate = getValues('vehicle.invoiceDate')
  const maxSpeedRequired = getValues('vehicle.isVehicleMaxSpeedRequired')
  const licensePlateTypeId = watch('vehicle.licensePlateTypeId')

  const [rzAndTpDisabled, setRzAndTpDisabled] = useState(false)
  useEffect(() => {
    if (licensePlateTypeId && licensePlateTypeId == Selection.PermanentlyUnallocated) {
      setRzAndTpDisabled(true)
      setValue('vehicle.licensePlate', '')
      setValue('vehicle.cisloTechnickehoPrukazu', '')
      setValue('vehicle.cisloOrv', '')
    }
    else
      setRzAndTpDisabled(false)

    //if (licensePlateTypeId && licensePlateTypes?.length && !licensePlateTypes.map(x => x.id).includes(licensePlateTypeId!)) {
    // výše uvedený includes vrací false, i když hodnota je v poli!!!
    if (licensePlateTypeId && licensePlateTypes?.length && !licensePlateTypes.find(x => x.id == licensePlateTypeId)) {
      setValue('vehicle.licensePlateTypeId', undefined)
    }
  }, [licensePlateTypeId])

  const inspectionDateRequired = watch('vehicle.isInspectionDateRequired')
  const otherPersonByInspection = watch('vehicle.otherPersonByInspection')
  const inspectionDate = watch('vehicle.inspectionDate')
  // Register for required validation
  register('vehicle.inspectionDate', {
    required: inspectionDateRequired ? 'Zadejte datum prohlídky vozidla.' : false,
  })

  const licensePlate = watch('vehicle.licensePlate')
  const cisloOrv = watch('vehicle.cisloOrv')
  const cisloTechnickehoPrukazu = watch('vehicle.cisloTechnickehoPrukazu')

  const isInspectionDistrictRequired = watch('vehicle.isInspectionDistrictRequired')
  const isPhotoDocumentationRequired = watch('vehicle.isPhotoDocumentationRequired')

  return (
    <>
      <FormGroup>
        <FormControl isInvalid={!!errors.vehicle?.vin} isRequired>
          <FormLabel tooltip="Zadejte identifikační číslo vozidla (VIN), které naleznete v technickém průkazu.">
            VIN
          </FormLabel>

          <InputGroup
            isDisabled={isContracted}
            id="vehicle.vin"
            htmlSize={18}
            maxLength={17}
            errors={errors.vehicle?.vin}
            {...register('vehicle.vin', {
              onChange: (e) => {
                setValue('vehicle.vin', e.target.value.toUpperCase())
              }, required: 'Zadejte VIN vozidla.',
            })}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.vehicle?.cisloTechnickehoPrukazu} isRequired={!!licensePlate && !cisloOrv}>
          <FormLabel>Číslo velkého TP</FormLabel>

          <InputGroup
            id="tp"
            htmlSize={10}
            placeholder="Číslo TP"
            {...register('vehicle.cisloTechnickehoPrukazu', {
              onChange: (e) => {
                setValue('vehicle.cisloTechnickehoPrukazu', e.target.value.toUpperCase(), { shouldValidate: true })
                trigger('vehicle.cisloOrv')
              },
            })}
            isDisabled={isContracted || rzAndTpDisabled}
            errors={errors.vehicle?.cisloTechnickehoPrukazu}
          />
        </FormControl>

        <Text variant="textMuted" sx={{ alignSelf: 'start', mt: 8, height: '30px' }}>
          nebo
        </Text>

        <FormControl isInvalid={!!errors.vehicle?.cisloOrv} isRequired={!!licensePlate && !cisloTechnickehoPrukazu}>
          <FormLabel>Číslo ORV</FormLabel>

          <InputGroup
            id="tp"
            htmlSize={10}
            placeholder="Číslo ORV"
            {...register('vehicle.cisloOrv', {
              onChange: (e) => {
                setValue('vehicle.cisloOrv', e.target.value.toUpperCase(), { shouldValidate: true })
                trigger('vehicle.cisloTechnickehoPrukazu')
              },
            })}
            isDisabled={isContracted || rzAndTpDisabled}
            errors={errors.vehicle?.cisloOrv}
          />
        </FormControl>
      </FormGroup>

      <FormGroup>
        <FormControl isInvalid={!!errors.vehicle?.licensePlate}>
          <FormLabel tooltip="Registrační značka (SPZ)">Reg. značka (SPZ)</FormLabel>

          <InputGroup
            id="spz"
            htmlSize={10}
            placeholder="SPZ"
            {...register('vehicle.licensePlate', {
              onChange: (e) => {
                setValue('vehicle.licensePlate', e.target.value.toUpperCase())
                trigger('vehicle.cisloTechnickehoPrukazu')
                trigger('vehicle.cisloOrv')
              },
            })}
            isDisabled={isContracted || rzAndTpDisabled}
            errors={errors.vehicle?.licensePlate}
          />
        </FormControl>

        {getValues('vehicle.isLicencePlateTypeRequired') && (
          <FormControl isRequired isInvalid={!!errors.vehicle?.licensePlateTypeId}>
            <FormLabel tooltip="Druh registrační značky (SPZ)">
              Druh registrační značky (SPZ)
            </FormLabel>

            <Select
              id="vehicle.licensePlateTypeId"
              {...register('vehicle.licensePlateTypeId')}
              isDisabled={isContracted}
              value={licensePlateTypeId?.toString() ?? ''}
            >
              <option disabled value="">
                Vyberte
              </option>
              {licensePlateTypes &&
                licensePlateTypes?.map((item) => (
                  <option value={item.id.toString()} key={item.id}>
                    {item.description}
                  </option>
                ))}
            </Select>

            {errors.vehicle?.licensePlateTypeId && (
              <FormErrorMessage>{errors.vehicle.licensePlateTypeId.message}</FormErrorMessage>
            )}
          </FormControl>
        )}

        {offer?.insurerId == Insurer.CPP && (
          <FormControl isInvalid={!!errors.vehicle?.purchaseMethod}>
            <FormLabel>Způsob pořízení vozdila</FormLabel>

            <Select
              id="vehicle.purchaseMethod"
              {...register('vehicle.purchaseMethod')}
              isDisabled={isContracted}
              value={watch('vehicle.purchaseMethod') ?? ''}
            >
              <option disabled value="">
                Vyberte
              </option>
              {dataPurchaseMethod &&
                dataPurchaseMethod?.map((item) => (
                  <option value={item.value?.toString()} key={item.id}>
                    {item.description}
                  </option>
                ))}
            </Select>

            {errors.vehicle?.purchaseMethod && (
              <FormErrorMessage>{errors.vehicle.purchaseMethod.message}</FormErrorMessage>
            )}
          </FormControl>
        )}

        {[Insurer.CPP, Insurer.PVZP].includes(offer?.insurerId) && (
          <FormControl isInvalid={!!errors.vehicle?.firstOwner} isDisabled={isContracted}>
            <FormLabel
              htmlFor="firstOwner"
              tooltip="Vyberte, zda je současný majitel prvním majitelem vozidla."
            >
              První majitel
            </FormLabel>

            <Controller
              name="vehicle.firstOwner"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onChange={(e) => onChange(e == '1')}
                  value={value == true ? '1' : value === false ? '0' : undefined}
                >
                  <RadioStack variant="stackedTile">
                    <Radio value={'1'}>Ano</Radio>
                    <Radio value={'0'}>Ne</Radio>
                  </RadioStack>
                </RadioGroup>
              )}
            />

            {errors.vehicle?.firstOwner && (
              <FormErrorMessage>{errors.vehicle.firstOwner.message}</FormErrorMessage>
            )}
          </FormControl>
        )}
      </FormGroup>

      <FormControl variant="fullWidth" isInvalid={!!errors.vehicle?.typeDescription}>
        <FormLabel>Bližší specifikace vozidla</FormLabel>

        <InputGroup
          isDisabled={isContracted}
          placeholder="Např. detailní model, motorizace apod."
          errors={errors.vehicle?.typeDescription}
          {...register('vehicle.typeDescription')}
        />
      </FormControl>

      {maxSpeedRequired && (
        <FormControl isInvalid={!!errors.vehicle?.vehicleMaxSpeed} isRequired>
          <FormLabel>Max. konstrukční rychlost</FormLabel>

          <InputGroup
            isDisabled={isContracted}
            id="vehicle.vehicleMaxSpeed"
            placeholder="Např. 25"
            htmlSize={6}
            autoComplete="off"
            inputMode="decimal"
            errors={errors.vehicle?.vehicleMaxSpeed}
            rightElement="km/h"
            {...register('vehicle.vehicleMaxSpeed')}
          />
        </FormControl>
      )}

      {inspectionDateRequired && (
        <FormControl
          isInvalid={!!errors.vehicle?.inspectionDate}
          isRequired={inspectionDateRequired}
        >
          <FormLabel htmlFor="vehicle.inspectionDate">Datum prohlídky vozidla</FormLabel>
          <Datepicker
            isDisabled={isContracted}
            errors={errors.vehicle?.inspectionDate}
            date={inspectionDate}
            onDateChange={(date) =>
              setValue('vehicle.inspectionDate', date, { shouldValidate: true })
            }
          />
        </FormControl>
      )}

      {isInspectionDistrictRequired && inspectionDistrictsData && (
        <FormControl>
          <FormLabel htmlFor="vehicle.inspectionDistrict">Místo prohlídky</FormLabel>

          <Select
            id="vehicle.inspectionDistrict"
            {...register('vehicle.inspectionDistrict')}
            isDisabled={isContracted}
          >
            <option disabled value="">
              Vyberte
            </option>
            {inspectionDistrictsData.map((item) => (
              <option value={item.value!.toString()} key={item.value}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {getValues('vehicle.isPreferredInspectionTypeRequired') &&
        offer?.insurerId == Insurer.Kooperativa && (
          <FormControl isInvalid={!!errors.vehicle?.preferredInspectionType}>
            <FormLabel>Preferovaný typ prohlídky</FormLabel>

            <Select
              id="vehicle.preferredInspectionType"
              {...register('vehicle.preferredInspectionType')}
              isDisabled={isContracted}
              defaultValue={getValues('vehicle.preferredInspectionType') ?? ''}
            >
              <option value="P">fotodokumentace vozidla pojistníkem</option>
              <option value="I">prohlídka technikem Global Expert</option>
            </Select>

            {errors.vehicle?.preferredInspectionType && (
              <FormErrorMessage>{errors.vehicle.preferredInspectionType.message}</FormErrorMessage>
            )}
          </FormControl>
        )}

      {(offer?.insurerId ?? 0) == Insurer.CPP && getValues('vehicle.isInspectionRequired') && (
        <FormGroup>
          <FormControl
            isInvalid={!!errors.vehicle?.otherPersonByInspection}
            isDisabled={isContracted}
            isRequired
          >
            <FormLabel
              htmlFor="otherPersonByInspection"
              tooltip="Vyberte, zda u prohlídky vozidla bude přítomna jiná osoba než pojistník."
            >Jiná osoba u prohlídky</FormLabel>

            <Controller
              name="vehicle.otherPersonByInspection"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onChange={(e) => onChange(e == '1')}
                  value={value == true ? '1' : value === false ? '0' : undefined}
                >
                  <RadioStack variant="stackedTile">
                    <Radio value={'1'}>Ano</Radio>
                    <Radio value={'0'}>Ne</Radio>
                  </RadioStack>
                </RadioGroup>
              )}
            />
            {errors.vehicle?.otherPersonByInspection && (
              <FormErrorMessage>
                {errors.vehicle.otherPersonByInspection.message}
              </FormErrorMessage>
            )}
          </FormControl>
          {otherPersonByInspection && (
            <FormControl isInvalid={!!errors.vehicle?.otherPersonPhone} isRequired>
              <FormLabel tooltip="Číslo mobilního telefonu osoby, která bude přítomna při prohlídce vozidla.">
                Mobilní telefon kontaktní osoby
              </FormLabel>

              <InputGroup
                id="otherPersonPhone"
                htmlSize={10}
                placeholder="Telefonní číslo"
                pattern="[\+0-9\s]+"
                {...register('vehicle.otherPersonPhone')}
                isDisabled={isContracted}
                errors={errors.vehicle?.otherPersonPhone}
              />
            </FormControl>
          )}
        </FormGroup>
      )}

      {offer?.insurerId == Insurer.Csob && isPhotoDocumentationRequired && photoDocumentationCodesData && (
        <SelectFormControl
          name="vehicle.photoDocumentationCode"
          control={control}
          label="Fotodokumentace vozidla"
          options={photoDocumentationCodesData.map(item => ({ value: item.value!.toString(), label: item.name }))}
          isDisabled={isContracted}
          isRequired
        />
      )}

      <FormSpacer />

      <Box
        sx={{
          w: '100%',
          display: 'grid',
          gridTemplateColumns: { base: '1fr', md: 'auto 1fr' },
          placeItems: 'start',
          gap: 8,
        }}
      >
        <FormControl>
          <FormLabel>Poškození vozidla</FormLabel>

          <CheckboxGroup isDisabled={isContracted}>
            <Checkbox {...register('vehicle.isDamaged')}>Vůz je v době sjednání poškozen</Checkbox>
            <Checkbox {...register('vehicle.hadSevereDamageInPast')}>
              {' '}
              Vůz byl v minulosti vážně poškozen
            </Checkbox>
          </CheckboxGroup>
        </FormControl>

        <FormControl variant="fullWidth">
          <FormLabel tooltip="Popis poškození">Popis poškození</FormLabel>

          <TextareaGroup
            isDisabled={isContracted}
            placeholder="Např. Oděrka na pravém předním blatníku..."
            {...register('vehicle.damageDescription')}
          />

          <FormHelperText>Tento popis bude uveden ve smlouvě.</FormHelperText>
        </FormControl>
      </Box>

      {getValues('vehicle.isInvoiceInfoRequired') && (
        <>
          <FormSpacer />

          <FormGroup>
            <FormControl isInvalid={!!errors.vehicle?.invoiceNumber} isRequired>
              <FormLabel tooltip="UNIQA vyžaduje doplnění údajů o faktuře, pokud byla z ní určena cena vozidla.">
                Číslo faktury
              </FormLabel>

              <InputGroup
                id="invoiceNumber"
                htmlSize={10}
                {...register('vehicle.invoiceNumber')}
                isDisabled={isContracted}
                errors={errors.vehicle?.invoiceNumber}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.vehicle?.invoiceDate} isRequired>
              <FormLabel htmlFor="vehicle.invoiceDate">Datum vystavení faktury</FormLabel>
              <Datepicker
                isDisabled={isContracted}
                errors={errors.vehicle?.invoiceDate}
                date={invoiceDate}
                onDateChange={(date) => setValue('vehicle.invoiceDate', date)}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.vehicle?.invoiceSelerIco} isRequired>
              <FormLabel>IČO prodejce</FormLabel>

              <InputGroup
                id="invoiceSelerIco"
                htmlSize={10}
                {...register('vehicle.invoiceSelerIco')}
                isDisabled={isContracted}
                errors={errors.vehicle?.invoiceSelerIco}
              />
            </FormControl>
          </FormGroup>
        </>
      )}
    </>
  )
}
//
